import { IconButton, styled as muiStyled } from '@mui/material';
import styled from 'styled-components';

// Styled Components
export const DataGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.greys.lightest};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.greys.light};
`;

export const GridContainer = styled.div`
  flex: 1;
  padding: 0 16px 0 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const PackagingContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  height: 64px;
`;

export const PackagingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px 8px 0px;
  border-radius: 6px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
  min-height: 56px;
  min-width: 180px;
  width: 180px;
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.brand.lightest : 'transparent'};
`;

export const PackagingIndicator = styled.div`
  width: 4px;
  height: 40px;
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.brand.primary : theme.colors.greys.medium};
  flex-shrink: 0;
`;

export const QuantityInputInline = styled.input`
  width: 60px;
  padding: 2px 4px;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 14px;
  outline: none;
  color: ${({ theme }) => theme.colors.greys.darkest};
  flex-shrink: 0;

  &:focus {
    background-color: ${({ theme }) => theme.colors.greys.lightest};
    border-radius: 2px;
  }
`;

export const QuantityDisplay = styled.div`
  min-width: 20px;
  text-align: center;
  font-size: 14px;
  flex-shrink: 0;
  color: ${({ theme, isEmpty, isDisabled }) =>
    isEmpty || isDisabled ? theme.colors.blacks.ipBlack4 : theme.colors.greys.darkest};
  font-family: ${({ isHighlighted }) => (isHighlighted ? 'inter-bold' : 'inter')};
`;

export const PackagingNameWrapper = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 120px;
  display: flex;
  align-items: center;
`;

export const PackagingName = styled.div`
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  white-space: normal;

  color: ${({ theme, isDisabled }) =>
    isDisabled ? theme.colors.blacks.ipBlack4 : theme.colors.greys.darkest};
  font: ${({ theme }) => theme.fonts.textBigHeight16};

  /* Alternative approach for multiline ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
`;

export const HorizontalButtonContainer = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

export const ActionButton = muiStyled(IconButton)`
  width: 40px;
  height: 40px;
  border: none;
  background-color: ${({ theme }) => theme.colors.greys.darkest};
  color: white !important;
  min-width: 32px;
  transition: background-color 0.2s ease;

  /* Style pour les composants Text à l'intérieur */
  & > * {
    color: white !important;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.brand.primary};

    & > * {
      color: white !important;
    }
  }

  &:active,
  &:focus {
    background-color: ${({ theme }) => theme.colors.brand.darker || theme.colors.brand.primary};

    & > * {
      color: white !important;
    }
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.greys.medium};
    color: white !important;
    opacity: 1;

    & > * {
      color: white !important;
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.greys.medium};
    }
  }
`;

export const DecrementButton = muiStyled(ActionButton)`
  border-radius: 8px 0 0 8px;
`;

export const IncrementButton = muiStyled(ActionButton)`
  border-radius: 0 8px 8px 0;
`;

export const getDatagridStyle = (theme, newlyAddedReferenceId) => ({
  height: '100%',
  width: '100%',
  border: '0px',
  '& .MuiDataGrid-main': {
    overflow: 'auto',
    scrollBehavior: 'auto',
  },
  '& .MuiDataGrid-virtualScroller': {
    scrollBehavior: 'auto',
    touchAction: 'pan-y pan-x',
    overscrollBehavior: 'contain',
    '&:focus-within': {
      scrollBehavior: 'auto',
    },
  },
  '& .MuiDataGrid-row': {
    '&:focus-within': {
      scrollMargin: '0 !important',
      scrollPadding: '0 !important',
    },
    [`&[data-id="${newlyAddedReferenceId}"]`]: {
      border: `1px solid ${theme.colors.brand.primary}`,
      borderRadius: '4px',
      transition: 'all 0.3s ease-in-out',
    },
  },
  '& .MuiDataGrid-cell': {
    '&:focus, &:focus-within': {
      outline: 'none',
      scrollMargin: '0 !important',
      scrollPadding: '0 !important',
    },
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
    {
      outline: 'none',
      scrollMargin: '0 !important',
      scrollPadding: '0 !important',
    },
  '& .MuiDataGrid-columnHeader': {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    color: '#6D706B',
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: '8px 16px',
  },
  '& .MuiDataGrid-footerContainer': {
    minHeight: '48px',
    height: '48px',
    borderTop: `1px solid ${theme.colors.greys.light}`,
    backgroundColor: theme.colors.greys.lightest,
    justifyContent: 'flex-start',
  },
  '& .MuiTablePagination-root': {
    fontSize: '14px',
  },
  '& .MuiTablePagination-displayedRows': {
    fontSize: '14px',
    margin: 0,
  },
  '& .MuiTablePagination-selectLabel': {
    fontSize: '14px',
    margin: 0,
  },
  '& .MuiTablePagination-actions': {
    marginLeft: '8px',
  },
});
